import { useEffect, useMemo, useRef, useState } from 'react';
import type MiniSearch from 'minisearch';
import type { Item } from '../db/types';
import { formatDueCompact } from '../lib/dates';
import { excerpt, search } from '../lib/search';
import type { Catalog } from '../lib/views';
import { Marked } from './TaskRow';
import './CommandPalette.css';

export interface Command {
  id: string;
  label: string;
  shortcut?: string;
  run: () => void;
}

interface Props {
  items: Item[];
  /** Builds the index on first use — see `useSearchIndex`. Only called when there is a query. */
  index: () => MiniSearch;
  /** Resolves list and tag ids back into names for the result metadata. */
  catalog: Catalog;
  commands: Command[];
  onOpenItem: (item: Item) => void;
  onClose: () => void;
}

type Entry =
  | { kind: 'item'; item: Item; terms: string[] }
  | { kind: 'command'; command: Command };

const MAX_PER_GROUP = 5;

/** Fuzzy search across everything, plus commands. Floating, centred, ~560px. */
export function CommandPalette({ items, index, catalog, commands, onOpenItem, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const { tasks, notes, matchedCommands, flat } = useMemo(() => {
    const q = query.trim();
    const hits = q ? search(index(), items, q) : [];
    const tasks = hits.filter((h) => h.item.kind === 'todo').slice(0, MAX_PER_GROUP);
    const notes = hits.filter((h) => h.item.kind === 'note').slice(0, MAX_PER_GROUP);
    const lower = q.toLowerCase();
    const matchedCommands = q
      ? commands.filter((c) => c.label.toLowerCase().includes(lower)).slice(0, MAX_PER_GROUP)
      : commands;

    const flat: Entry[] = [
      ...tasks.map((h) => ({ kind: 'item' as const, item: h.item, terms: h.terms })),
      ...notes.map((h) => ({ kind: 'item' as const, item: h.item, terms: h.terms })),
      ...matchedCommands.map((command) => ({ kind: 'command' as const, command })),
    ];
    return { tasks, notes, matchedCommands, flat };
  }, [query, items, index, commands]);

  // Any change to the result set resets the cursor to the top match.
  useEffect(() => setCursor(0), [query]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>('[data-cursor="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  function choose(entry: Entry | undefined) {
    if (!entry) return;
    if (entry.kind === 'item') onOpenItem(entry.item);
    else entry.command.run();
    onClose();
  }

  return (
    <div className="palette__scrim" onMouseDown={onClose}>
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="palette__field">
          <span className="palette__sigil" aria-hidden="true">
            ⌕
          </span>
          <input
            className="palette__input"
            autoFocus
            value={query}
            spellCheck={false}
            placeholder="Search everything, or run a command…"
            aria-label="Search"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(flat.length - 1, c + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(0, c - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                choose(flat[cursor]);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
              }
            }}
          />
          <button type="button" className="kbd" onClick={onClose}>
            esc
          </button>
        </div>

        <div className="palette__results" ref={listRef}>
          {flat.length === 0 && (
            <div className="palette__empty">
              <div className="empty__title">No matches for “{query}”</div>
              <div className="empty__hint">Search covers titles, bodies and tags.</div>
            </div>
          )}

          {tasks.length > 0 && <div className="palette__group">TASKS</div>}
          {tasks.map((hit, i) => (
            <Row key={hit.item.id} active={cursor === i} onPick={() => choose(flat[i])} index={i}>
              {hit.item.priority < 4 ? (
                <span className={`pri-dot pri-dot--${hit.item.priority}`} />
              ) : (
                <span className="pri-dot" />
              )}
              <span className="palette__label">
                <Marked text={hit.item.title} terms={hit.terms} />
              </span>
              <span className="palette__meta">
                {[
                  hit.item.due_at ? formatDueCompact(hit.item.due_at) : '',
                  catalog.lists.find((l) => l.id === hit.item.list_id)?.name,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </Row>
          ))}

          {notes.length > 0 && <div className="palette__group">NOTES</div>}
          {notes.map((hit, i) => {
            const at = tasks.length + i;
            return (
              <Row key={hit.item.id} active={cursor === at} onPick={() => choose(flat[at])} index={at}>
                <span className="palette__square" />
                <span className="palette__label">
                  <Marked text={hit.item.title} terms={hit.terms} />
                </span>
                <span className="palette__meta">
                  <Marked text={excerpt(hit.item.body, hit.terms, 32)} terms={hit.terms} />
                </span>
              </Row>
            );
          })}

          {matchedCommands.length > 0 && <div className="palette__group">COMMANDS</div>}
          {matchedCommands.map((command, i) => {
            const at = tasks.length + notes.length + i;
            return (
              <Row key={command.id} active={cursor === at} onPick={() => choose(flat[at])} index={at}>
                <span className="palette__label palette__label--command">{command.label}</span>
                {command.shortcut && <span className="kbd">{command.shortcut}</span>}
              </Row>
            );
          })}
        </div>

        <div className="palette__foot">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc dismiss</span>
        </div>
      </div>
    </div>
  );
}

function Row({
  active,
  index,
  onPick,
  children,
}: {
  active: boolean;
  index: number;
  onPick: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`palette__row${active ? ' is-active' : ''}`}
      data-cursor={active}
      data-index={index}
      role="option"
      aria-selected={active}
      onClick={onPick}
    >
      {children}
    </div>
  );
}
